import { useContext, useEffect, useRef } from "react";
import * as turf from "@turf/turf";
import MapboxDraw from "@mapbox/mapbox-gl-draw";

// components
import MapContext from "../../../services/context/map-context";
import DrawAssistedRectangle from "../../map/drawRecLine";
import { formatArea } from "../../../utils/formatting";

// constants
import {
  DRAWMODE,
  PROPERTY_SOURCE,
} from "../../../constants/site-settings.constants";

// context
import { useAppContext } from "../../../services/context/app-context";

// utils
import {
  addClipLayer,
  addPropertyAreaLabel,
  addPropertyDistanceLabels,
  addPropertyVertexLabels,
  isMapExist,
  removeAllPropertyLabels,
  removeClipLayer,
} from "../../../utils/map-layer";
import {
  createDistanceAndVertexFeatures,
  reverseGeocode,
} from "../../../utils/map-labels";

// style
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import { drawTheme } from "./draw-theme";

export const DrawProperty = ({
  propertyGeoJSON,
  updatePropertyData,
  drawMode,
  setDrawMode,
  deleteDrawing,
  setDeleteDrawing,
  setIsValidPolygon,
}) => {
  const { map, mapLoaded } = useContext(MapContext);
  const { setPropertyLocation, showBaseBuildings } = useAppContext();
  const drawRef = useRef(null);

  useEffect(() => {
    if (!map || !mapLoaded) return;
    // Initialize MapboxDraw
    if (!drawRef.current) {
      drawRef.current = new MapboxDraw({
        displayControlsDefault: false,
        defaultMode: DRAWMODE.polygon,
        modes: Object.assign(
          {
            draw_assisted_rectangle: DrawAssistedRectangle,
          },
          MapboxDraw.modes,
        ),
        styles: [...drawTheme],
      });
      map.addControl(drawRef.current);
      map.getCanvas().style.cursor = "crosshair";

      if (propertyGeoJSON) {
        displayProperty();
      }

      map.on("draw.create", updateProperty);
      map.on("draw.update", updateProperty);
      map.on("draw.delete", clearProperty);
      map.on("draw.modechange", onModeChange);
    }

    /*   map.setConfigProperty(
        'basemap',
        'showPlaceLabels',
        false
      );
      map.setConfigProperty(
        'basemap',
        'showPointOfInterestLabels',
        false
      );
      map.setConfigProperty(
        'basemap',
        'showRoadLabels',
        false
      );
      map.setConfigProperty(
        'basemap',
        'showTransitLabels',
        false
      ); */

    // Cleanup on unmount
    return () => {
      if (map && mapLoaded && drawRef.current && isMapExist(map)) {
        if (typeof map.off === "function") {
          map.off("draw.create", updateProperty);
          map.off("draw.update", updateProperty);
          map.off("draw.delete", clearProperty);
          map.off("draw.modechange", onModeChange);
        }

        // Remove any label layers and sources
        removeAllPropertyLabels(map);
        removeClipLayer(map);

        map.getCanvas().style.cursor = "";
        map.removeControl(drawRef.current);
        drawRef.current = null;
      }
    };
  }, [map, mapLoaded]);

  /*   useEffect(() => {
    if (!map || !mapLoaded) return;

    if (drawRef.current) {
      drawRef.current.deleteAll();
       // Reset to the same mode
      map.getCanvas().style.cursor = "crosshair";

      // Remove any label layers and sources
      removeAllPropertyLabels(map);
      removeClipLayer(map);
    }

    if (propertyGeoJSON) {
      displayProperty();
    }
  }, [propertyGeoJSON]); */

  // hide base buildings
  useEffect(() => {
    if (!showBaseBuildings && drawRef.current && propertyGeoJSON) {
      addClipLayer(map, propertyGeoJSON);
    }
    if (showBaseBuildings) {
      removeClipLayer(map);
    }
  }, [showBaseBuildings]);

  // clear existing drawing
  useEffect(() => {
    if (deleteDrawing) {
      clearProperty();
      setIsValidPolygon(true);
    }
  }, [deleteDrawing]);

  const onModeChange = (e) => {
    const mode = e.mode;
    if (mode === "draw_polygon" || mode === "draw_assisted_rectangle") {
      map.getCanvas().style.cursor = "crosshair";
    } else {
      // Reset cursor to default
      map.getCanvas().style.cursor = "pointer";
    }
  };

  const updateProperty = async (e) => {
    const drawFeatures = drawRef.current.getAll();
    const polygon = e.features[0];
    if (!polygon) return;

    const invalid = turf.kinks(polygon); // returns a FeatureCollection of intersections

    if (invalid && invalid.features.length > 0) {
      // It's self-intersecting
      setIsValidPolygon(false);
    } else {
      setIsValidPolygon(true);
    }

    // Distance & vertex labeling
    createLabelsForProperty(polygon);

    // Calculate area & perimeter
    const area = turf.area(polygon);
    const lines = turf.polygonToLine(polygon);
    const length = turf.length(lines, { units: "miles" });
    const coordinates = polygon.geometry.coordinates[0];

    let perimeter = 0;
    for (let i = 0; i < coordinates.length - 1; i++) {
      perimeter += turf.distance(
        turf.point(coordinates[i]),
        turf.point(coordinates[i + 1]),
        { units: "meters" },
      );
    }

    // Get center
    const center = turf.center(polygon);
    const mainCoordinates = center
      ? {
          lat: center.geometry.coordinates[1],
          long: center.geometry.coordinates[0],
        }
      : null;

    const address = await reverseGeocode(center.geometry.coordinates);

    // Update the context or local state
    updatePropertyData("perimeter", Math.round(perimeter * 100) / 100);
    updatePropertyData("propertyArea", Math.round(area * 100) / 100);
    updatePropertyData("coordinatesCenter", mainCoordinates);
    updatePropertyData("location", address ? address : null);
    updatePropertyData("propertyGeoJSON", drawFeatures);
    setPropertyLocation(center ? center.geometry.coordinates : null);

    // Add a clip layer (eraser) for the polygon
    if (!showBaseBuildings) {
      addClipLayer(map, drawFeatures);
    }
  };

  const createLabelsForProperty = (polygon) => {
    const { distanceFeatures, vertexFeatures } =
      createDistanceAndVertexFeatures(polygon);
    const area = turf.area(polygon);
    const centroid = turf.center(polygon);

    addPropertyDistanceLabels(map, {
      type: "FeatureCollection",
      features: distanceFeatures,
    });

    addPropertyVertexLabels(map, {
      type: "FeatureCollection",
      features: vertexFeatures,
    });

    addPropertyAreaLabel(map, {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: centroid.geometry,
          properties: {
            area: `${formatArea(area).value} ${formatArea(area).unit}`,
          },
        },
      ],
    });
  };

  const displayProperty = () => {
    // Add the existing propertyGeoJSON
    if (!drawRef.current || !propertyGeoJSON) return;
    drawRef.current.add(propertyGeoJSON);
    const featureId = propertyGeoJSON.features[0].id;

    // Switch mode to direct_select for the existing polygon
    if (featureId) drawRef.current.changeMode(DRAWMODE.select, { featureId });
    map.getCanvas().style.cursor = "";

    // Create labels for the existing polygon
    const polygon = propertyGeoJSON.features[0];
    createLabelsForProperty(polygon);

    // Add eraser
    if (!showBaseBuildings) {
      addClipLayer(map, propertyGeoJSON);
    }

    /*  // Compute centroid if it's a polygon
      const centroid = turf.centroid(polygon);
      
        // Default text offset
        let textOffset = [0, -1]; // default

        if (centroid) {
          // Calculate direction vector from centroid to point
          const direction = [
            coord[0] - centroid.geometry.coordinates[0],
            coord[1] - centroid.geometry.coordinates[1],
          ];

          // Normalize the direction vector
          const length = Math.hypot(direction[0], direction[1]);
          const unitDirection = [direction[0] / length, direction[1] / length];

          // Set offset magnitude
          const offsetMagnitude = 1.5;

          // Calculate text offset in ems
          textOffset = [unitDirection[0] * offsetMagnitude, unitDirection[1] * offsetMagnitude];
        }
 */
  };

  const clearProperty = () => {
    if (!drawRef.current) return;
    drawRef.current.deleteAll();
    drawRef.current.changeMode(drawMode); // Reset to the same mode
    map.getCanvas().style.cursor = "crosshair";

    // Remove any label layers and sources
    removeAllPropertyLabels(map);
    removeClipLayer(map);

    // Clear property data

    updatePropertyData("propertyGeoJSON", null);
    updatePropertyData("propertyArea", null);
    updatePropertyData("area", null);
    updatePropertyData("perimeter", null);

    updatePropertyData("uploadedPropertyGeoJSON", null);
    updatePropertyData("propertySource", PROPERTY_SOURCE.HAND_DRAWN);
    updatePropertyData("fileId", null);
    updatePropertyData("units", null);
    updatePropertyData("pivot", null);
    updatePropertyData("center", null);
    updatePropertyData("uploadedPropertyArea", null);
    updatePropertyData("uploadedPropertyPerimeter", null);

    setDeleteDrawing(false);
  };

  return null;
};
